/**
 * ProfileSection (#553) — one stacked section card on the Pet Profile page.
 * One elevation idiom (raised card, `--ferro-shadow-1`), one heading style,
 * an optional single action slot (Principle 2 — at most one primary per view;
 * section actions are ghost). Structural tokens only.
 *
 * Honest empty state (Principle 5): pass `isEmpty` + `emptyText` and the
 * section renders teaching copy instead of a blank — never an invented value.
 */
import type { ReactNode } from 'react';

interface ProfileSectionProps {
  title: string;
  /** Optional right-aligned ghost action (e.g. "Add note"). */
  action?: ReactNode;
  /** When true, renders `emptyText` instead of `children`. */
  isEmpty?: boolean;
  /** Teaching copy shown when empty — say what to do next. */
  emptyText?: string;
  /** Optional loud safety banner rendered above the body (allergies/banned). */
  banner?: ReactNode;
  children?: ReactNode;
  testId?: string;
}

export function ProfileSection({
  title,
  action,
  isEmpty = false,
  emptyText,
  banner,
  children,
  testId,
}: ProfileSectionProps): JSX.Element {
  return (
    <section className="ferro-dogprofile__section" data-testid={testId}>
      <div className="ferro-dogprofile__section-head">
        <h2 className="ferro-dogprofile__section-title">{title}</h2>
        {action ? (
          <div className="ferro-dogprofile__section-action">{action}</div>
        ) : null}
      </div>
      {banner}
      {isEmpty ? (
        <p className="ferro-dogprofile__empty">{emptyText}</p>
      ) : (
        children
      )}
    </section>
  );
}
